/**
 * Cover grid for the /reranker tool: the default results view. One card per
 * scored book with a large cover, rank, score bar, winning signal and pipeline
 * flags; a click expands the feature breakdown. Books the warm pipeline would
 * exclude stay visible but dimmed.
 */
import { BookOpen } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../i18n/format';
import type { AffinityBookRow, AffinityFlag, AffinityScoreType } from '../services/api';
import AffinityReasons from './AffinityReasons';
import { coverUrl, FEATURE_ORDER, FLAG_STYLES, scoreBarScale } from './affinityPresentation';

function Cover({ row }: Readonly<{ row: AffinityBookRow }>) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="aspect-[2/3] w-full rounded-lg bg-gray-100 flex items-center justify-center">
        <BookOpen size={28} className="text-gray-400" />
      </div>
    );
  }
  return (
    <img
      src={coverUrl(row, 400)}
      alt={row.title || row.book_id}
      loading="lazy"
      className="aspect-[2/3] w-full rounded-lg object-cover shadow-sm"
      onError={() => setFailed(true)}
    />
  );
}

function Flag({ flag }: Readonly<{ flag: AffinityFlag }>) {
  const { t } = useTranslation();
  return (
    <span
      className={`inline-block rounded-full border px-1.5 py-0.5 text-[10px] leading-tight ${FLAG_STYLES[flag]}`}
      title={t(`affinity.flagHints.${flag}`)}
    >
      {t(`affinity.flags.${flag}`)}
    </span>
  );
}

function Features({ features }: Readonly<{ features: Record<string, number | null> }>) {
  const { t } = useTranslation();
  const present = FEATURE_ORDER.filter(
    (key) => features[key] !== null && features[key] !== undefined
  );
  return (
    <dl className="mt-2 grid grid-cols-2 gap-x-2 gap-y-0.5 text-[11px] text-gray-500">
      {present.map((key) => (
        <div key={key} className="flex justify-between gap-1">
          <dt className="truncate text-gray-400">{t(`affinity.features.${key}`)}</dt>
          <dd className="tabular-nums text-gray-700">{formatNumber(features[key] as number)}</dd>
        </div>
      ))}
    </dl>
  );
}

function Card({
  row,
  pct,
  scoreHint,
}: Readonly<{ row: AffinityBookRow; pct: number; scoreHint?: string }>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      aria-expanded={open}
      className={`group text-left rounded-xl border border-gray-200 bg-white p-3 transition hover:border-indigo-brand-text hover:shadow-md ${
        row.excluded_by_pipeline ? 'opacity-50' : ''
      }`}
    >
      <div className="relative">
        <Cover row={row} />
        <span className="absolute left-2 top-2 rounded-full bg-white/90 px-2 py-0.5 text-xs font-semibold tabular-nums text-gray-800 shadow-sm">
          #{row.rank + 1}
        </span>
        <span
          className="absolute right-2 top-2 rounded-full bg-indigo-brand-text px-2 py-0.5 text-xs font-semibold tabular-nums text-white shadow-sm"
          title={scoreHint}
        >
          {formatNumber(row.score)}
        </span>
      </div>
      <div className="mt-2 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full bg-indigo-brand-text" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-2 text-sm font-medium text-gray-900 line-clamp-2">
        {row.title || row.book_id}
      </p>
      <p className="text-xs text-gray-500 truncate">{row.author || '—'}</p>
      <p className="mt-1 text-[11px] text-gray-400">
        {t(`affinity.signals.${row.winning_signal}`)}
        {row.age_group ? ` · ${row.age_group}` : ''}
      </p>
      <div className="mt-1.5">
        <AffinityReasons row={row} size="xs" />
      </div>
      {row.flags.length > 0 ? (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {row.flags.map((flag) => (
            <Flag key={flag} flag={flag} />
          ))}
        </div>
      ) : null}
      {open ? (
        <>
          <p className="mt-2 text-[11px] font-mono text-gray-400 truncate">
            {row.book_id}
            {row.primary_subject ? ` · ${row.primary_subject}` : ''}
          </p>
          <Features features={row.features} />
        </>
      ) : null}
    </button>
  );
}

interface AffinityGridProps {
  rows: AffinityBookRow[];
  scoreType?: AffinityScoreType;
}

export default function AffinityGrid({ rows, scoreType }: Readonly<AffinityGridProps>) {
  const { t } = useTranslation();
  const barPercent = useMemo(() => scoreBarScale(rows, scoreType), [rows, scoreType]);
  const scoreHint = scoreType === 'rank' ? t('affinity.results.scoreRankHint') : undefined;
  if (rows.length === 0) {
    return <p className="text-sm text-gray-500 py-8 text-center">{t('affinity.results.empty')}</p>;
  }
  return (
    <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
      {rows.map((row) => (
        <Card key={row.book_id} row={row} pct={barPercent(row.score)} scoreHint={scoreHint} />
      ))}
    </div>
  );
}
